// ═══════════════════════════════════════
// Seed de roles default — abril 2026
// ═══════════════════════════════════════
// Crea el set base de roles si no existen. Es idempotente:
//   - Si un rol con el mismo `codigo` ya existe, no lo toca (salvo que forzarActualizacion=true).
//   - Si no existe, lo crea con los valores canónicos de esta semilla.
//
// Roles que crea:
//   - suspendido              → temporal automático (empresa en suspensión)
//   - duenoCliente            → dueño/admin del lado cliente
//   - empleadoCliente         → empleado estándar del cliente
//   - colaboradorStandard     → colaborador externo (contador, carpintero, freelance)
//   - consultorMilbrands      → consultor MilBrands (operador)
//   - supervisorMilbrands     → supervisor MilBrands (recibe escalados SLA)
//
// Los permisos quedan vacíos en esta fase: la Fase 2 completa roles.permisos[] con la
// estructura { moduloCodigo, funcionCodigo, scope }. Por ahora solo creamos el registro
// para que los IDs existan y estadoEmpresa.js pueda referenciar el rol Suspendido.
//
// Uso:
//   import { seedRolesDefault } from '@/lib/seedRoles'
//   await seedRolesDefault()                   // idempotente
//   await seedRolesDefault({ dryRun: true })   // solo loguea
// ═══════════════════════════════════════

import {
  collection, getDocs, setDoc, updateDoc, doc, query, where, serverTimestamp,
} from 'firebase/firestore'
import { db } from './firebase'
import { COLLECTIONS, ROL_SUSPENDIDO, ROL_SUSPENDIDO_CODIGO } from './constants'

// Definición canónica de los roles base.
// `permisos` queda [] a propósito — se completa en Fase 2 (ABM de módulos + puedeHacer).
export const ROLES_DEFAULT = [
  ROL_SUSPENDIDO,
  {
    codigo: 'duenoCliente',
    nombre: 'Dueño Cliente',
    color: '#0EA5E9',
    orden: 10,
    activo: true,
    aplicaA: ['cliente'],    esSistema: true,
    descripcion: 'Dueño / admin del lado cliente. Puede gestionar su empresa, usuarios y colaboradores.',
    permisos: [],
  },
  {
    codigo: 'empleadoCliente',
    nombre: 'Empleado Cliente',
    color: '#22C55E',
    orden: 20,
    activo: true,
    aplicaA: ['cliente'],    esSistema: true,
    descripcion: 'Empleado estándar del cliente. Permisos finos se ajustan en el perfil o vía asignaciones.',
    permisos: [],
  },
  {
    codigo: 'colaboradorStandard',
    nombre: 'Colaborador Standard',
    color: '#A855F7',
    orden: 30,
    activo: true,
    aplicaA: ['colaborador'],    esSistema: true,
    descripcion: 'Colaborador externo del cliente (contador, freelance, proveedor). Puede pertenecer a una o varias empresas.',
    permisos: [],
  },
  {
    codigo: 'consultorMilbrands',
    nombre: 'Consultor MilBrands',
    color: '#F59E0B',
    orden: 40,
    activo: true,
    aplicaA: ['consultor'],  // equivalente a 'milbrands'    esSistema: true,
    descripcion: 'Operador del equipo MilBrands. Trabaja en la cuenta del cliente según asignaciones.',
    permisos: [],
  },
  {
    codigo: 'supervisorMilbrands',
    nombre: 'Supervisor MilBrands',
    color: '#EF4444',
    orden: 50,
    activo: true,
    aplicaA: ['consultor'],    esSistema: true,
    descripcion: 'Supervisor del equipo MilBrands. Recibe escalados por SLA vencidos y puede reasignar.',
    permisos: [],
  },
]

/**
 * Crea los roles default en Firestore si no existen.
 *
 * @param {Object} opts
 * @param {boolean} [opts.dryRun=false]             Solo loguea sin escribir.
 * @param {boolean} [opts.forzarActualizacion=false] Si un rol ya existe, pisa nombre/color/descripcion
 *                                                   (nunca pisa `permisos` para no perder lo que hizo el admin).
 * @returns {Promise<{creados: string[], existentes: string[], actualizados: string[]}>}
 */
export async function seedRolesDefault(opts = {}) {
  const { dryRun = false, forzarActualizacion = false } = opts

  console.groupCollapsed(`[seedRolesDefault] dryRun=${dryRun} forzar=${forzarActualizacion}`)

  // Traer todos los roles existentes una vez (menos ida y vuelta que query por cada uno)
  const snap = await getDocs(collection(db, COLLECTIONS.ROLES))
  const existentesPorCodigo = new Map()
  snap.forEach(d => {
    const data = d.data()
    if (data.codigo) existentesPorCodigo.set(data.codigo, { ...data, id: d.id })
  })

  const creados = []
  const existentes = []
  const actualizados = []

  for (const rolDef of ROLES_DEFAULT) {
    const existente = existentesPorCodigo.get(rolDef.codigo)

    if (existente) {
      if (forzarActualizacion) {
        const updates = {
          nombre: rolDef.nombre,
          color: rolDef.color,
          orden: rolDef.orden,
          aplicaA: rolDef.aplicaA,
          descripcion: rolDef.descripcion,
          esSistema: rolDef.esSistema,
          actualizadoEn: serverTimestamp(),
          // NO pisamos: permisos, activo (los maneja el admin)
        }
        if (!dryRun) {
          await updateDoc(doc(db, COLLECTIONS.ROLES, existente.id), updates)
        }
        actualizados.push(rolDef.codigo)
        console.log(`[seedRolesDefault] actualizado: ${rolDef.codigo}`, updates)
      } else {
        existentes.push(rolDef.codigo)
        console.log(`[seedRolesDefault] ya existe: ${rolDef.codigo} (id=${existente.id})`)
      }
      continue
    }

    // No existe → crear
    const payload = {
      ...rolDef,
      creadoEn: serverTimestamp(),
    }
    if (!dryRun) {
      // El Suspendido va con el mismo id fijo que le da `resolverRolSuspendidoId`
      // (estadoEmpresa.js): por cualquiera de los dos caminos queda uno solo.
      const ref = rolDef.codigo === ROL_SUSPENDIDO_CODIGO
        ? doc(db, COLLECTIONS.ROLES, ROL_SUSPENDIDO_CODIGO)
        : doc(collection(db, COLLECTIONS.ROLES))
      await setDoc(ref, payload)
      console.log(`[seedRolesDefault] creado: ${rolDef.codigo} → id=${ref.id}`)
    } else {
      console.log(`[seedRolesDefault] PLANEADO crear: ${rolDef.codigo}`, payload)
    }
    creados.push(rolDef.codigo)
  }

  console.log(`[seedRolesDefault] creados=${creados.length} existentes=${existentes.length} actualizados=${actualizados.length}`)
  console.groupEnd()

  return { creados, existentes, actualizados }
}

/**
 * Busca el id de un rol por su código (helper utilitario).
 * Devuelve null si no existe.
 */
export async function getRolIdPorCodigo(codigo) {
  const q = query(collection(db, COLLECTIONS.ROLES), where('codigo', '==', codigo))
  const snap = await getDocs(q)
  if (snap.empty) return null
  return snap.docs[0].id
}

// Conveniencia: exponer en window para poder correrlo desde la consola del navegador
if (typeof window !== 'undefined') {
  window.__seedRolesDefault = seedRolesDefault
  window.__getRolIdPorCodigo = getRolIdPorCodigo
}
